import * as React from 'react';
import {Field, formValueSelector} from 'redux-form';
import {connect} from 'react-redux';
import {flow, forEachRight, map} from 'lodash';
import {II18nMessages} from '../../../../../../services/branding/models';
import {Col, ControlLabel, FormGroup, OverlayTrigger, Popover, Row} from 'react-bootstrap';
import FieldFormControl from '../../../../../../components/FieldFormControl/index';
import SelectControl from '../../../../../../components/SelectControl/index';
import * as uuid from 'uuid/v4';
import {isRequired, isAdult, isValidDate, isPostcode, isPhone, isEmail} from '../../../../../../util/validators';
import {IChannel, IPostcodeAddress, IProduct} from '../../../../../../data/api/models';
import PostcodeControl from '../../../../../../components/PostcodeControl';
import FaPencil from 'react-icons/fa/pencil';
import {Link} from 'react-router-dom';
import FaClose from 'react-icons/fa/close';
import FaInfo from 'react-icons/fa/info';
import FaInfoCircle from 'react-icons/fa/info-circle';
import * as Commonmark from 'react-commonmark';
type TravellerType = 'child' | 'adult';

interface ITravellerTypeConfig {
  min: number;
  max: number;
}

interface IGroupType {
  adult: ITravellerTypeConfig;
  child?: ITravellerTypeConfig;
}

interface IGroupTypeMap {
  [type: string]: IGroupType;
}

interface ITripDetailsForm {
  i18n: II18nMessages;
  pristine: boolean;
  fields: any;
  groupType: string;
  tripType: string;
  product: IProduct;
  change: any;
  channel: IChannel;
}

const groupTypeTravellerMap: IGroupTypeMap = {
  individual: {
    adult: {
      min: 1,
      max: 10,
    },
    child: {
      min: 0,
      max: 10,
    },
  },
  couple: {
    adult: {
      min: 2,
      max: 2,
    },
    child: {
      min: 0,
      max: 0,
    },
  },
  single_parent_family: {
    adult: {
      min: 1,
      max: 1,
    },
    child: {
      min: 1,
      max: 10,
    },
  },
  family: {
    adult: {
      min: 2,
      max: 2,
    },
    child: {
      min: 1,
      max: 10,
    },
  },
  independent_child: {
    adult: {
      min: 0,
      max: 0,
    },
    child: {
      min: 1,
      max: 10,
    },
  },
};

const defaultTraveller = {
  id: '',
  title: '',
  firstName: '',
  lastName: '',
  dob: '',
  phone: '',
  email: '',
  residency: '',
  type: '',
  screening: null,
  screeningSessionId: null,
};

const childTitles = [
  {label: 'Master', value: 'Master'},
  {label: 'Mx', value: 'Mx'},
  {label: 'Miss', value: 'Miss'},
  {label: 'Mr', value: 'Mr'},
];

const adultTitles = [
  {label: 'Mr', value: 'Mr'},
  {label: 'Mrs', value: 'Mrs'},
  {label: 'Miss', value: 'Miss'},
  {label: 'Ms', value: 'Ms'},
  {label: 'Master', value: 'Master'},
  {label: 'Mx', value: 'Mx'},
  {label: 'Dr', value: 'Dr'},
  {label: 'Prof', value: 'Prof'},
  {label: 'Rev', value: 'Rev'},
  {label: 'Cpt', value: 'Cpt'},
  {label: 'Sir', value: 'Sir'},
  {label: 'Dame', value: 'Dame'},
  {label: 'Lord', value: 'Lord'},
  {label: 'Lady', value: 'Lady'},
  {label: 'Rt Honourable', value: 'Rt Honourable'},
];

class TravellersInfo extends React.Component<ITripDetailsForm> {
  private getAll = (type: TravellerType, fields) => fields.getAll().filter((traveller) => traveller.type === type);
  private count = (type: TravellerType, fields) => this.getAll(type, fields).length;
  private handleTravellerUpdates = (type: TravellerType, config: ITravellerTypeConfig, fields) => {
    let total = this.count(type, fields);

    if (total > config.max) {
      // Remove travellers from the form until the maximum amount of travellers are
      // achieved for the next group type.

      forEachRight(this.getAll(type, fields), (traveller) => {
        if (total <= config.max) {
          return;
        }

        this.removeField(traveller.id, fields);
        total -= 1;
      });
    }

    if (total < config.min) {
      const totalToAdd = config.min - total;

      for (let i = 0; i < totalToAdd; i++) {
        this.addField(type, fields);
      }
    }
  }

  public componentWillReceiveProps(nextProps: ITripDetailsForm) {
    if (this.props.groupType === nextProps.groupType) {
      return;
    }

    const {fields, groupType} = nextProps;
    const config = groupTypeTravellerMap[groupType ? groupType : 'individual'];

    if (config) {
      const {adult, child} = config;

      this.handleTravellerUpdates('adult', adult, fields);
      this.handleTravellerUpdates('child', child, fields);
    }
  }

  private addField = (type: TravellerType, fields) => {
    fields.push({
      ...defaultTraveller,
      id: uuid(),
      type,
    });
  }

  private getIndex = (id: string, fields) => {
    let travellerIndex = -1;
    const travellers = fields.getAll();

    travellers.forEach((traveller, index) => {
      if (traveller.id === id) {
        travellerIndex = index;
      }
    });

    if (travellerIndex === -1) {
      console.error('Couldn\'t find traveller', travellers, id, travellerIndex);
    }

    return travellerIndex;
  }

  private removeField = (id: string, fields) => {
    const travellerIndex = this.getIndex(id, fields);

    if (travellerIndex === -1) {
      return;
    }

    fields.remove(travellerIndex);
  }

  private handleSelectAddress = (address: IPostcodeAddress) => {
    const {change} = this.props;
    const base = 'travellers[0].address';

    change(`${base}.line1`, address.line1);
    change(`${base}.line2`, address.line2);
    change(`${base}.town`, address.town);
    change(`${base}.county`, address.county);
    change(`${base}.postcode`, address.postcode);
  }

  private onClose = () => {
    document.body.click();
  }

  private renderDefinition = (name: string, icon?: string) => {
    const { product } = this.props;
    const definition = product.metadata.definitions[name];

    if (!definition) {
      return null;
    }
    const popover = (
        <Popover
            id={`popover-${name}`}
            onClick={this.onClose}
        >
          <div className="close-container">
            <div className="close-tooltip">
              <FaClose/>
            </div>
            <Commonmark
                source={definition}
                linkTarget="_blank"
                // transformLinkUri={this.transformLinks}
            />
          </div>
        </Popover>
    );

    return (
        <OverlayTrigger
          trigger="click"
          rootClose={true}
          overlay={popover}
          // placement={isMobileView ? 'bottom' : 'right'}
          // positionLeft={200}
          // positionTop={50}
        >
          {icon === 'FaInfo' ? <FaInfo className="info-btn-helper"/> : <FaInfoCircle className="info-btn"/>}
        </OverlayTrigger>
    );
  }

  private renderLeadTravellerFields = (traveller, fieldIndex) => {
    const { i18n, product } = this.props;
    const residencies = map(product.metadata.residencies, (item, key) => ({
      label: item,
      value: key,
    }));
    const postcodeValidators = [
      isRequired,
      isPostcode,
    ];

    return (
      <div style={{marginLeft: '10px'}}>
        <Row className="margin-top">
          <FormGroup controlId="postcode">
            <Col componentClass={ControlLabel} sm={3}>
              <div style={{marginLeft: '5px'}}>{i18n.quote.tripDetails.traveller.phone}:</div>
            </Col>
            <Col sm={6}>
              <Field
                name={`travellers[${fieldIndex}].phone`}
                type="number"
                component={FieldFormControl}
                validate={[isRequired, isPhone]}
              />
            </Col>
          </FormGroup>
        </Row>
        <Row className="margin-top">
          <FormGroup controlId="postcode">
            <Col componentClass={ControlLabel} sm={3}>
              <div style={{marginLeft: '5px'}}>{i18n.quote.tripDetails.traveller.email}:</div>
            </Col>
            <Col sm={6}>
              <Field
                name={`travellers[${fieldIndex}].email`}
                type="text"
                component={FieldFormControl}
                validate={[isRequired, isEmail]}
              />
            </Col>
          </FormGroup>
        </Row>
        <Row className="margin-top">
          <FormGroup controlId="postcode">
            <Col componentClass={ControlLabel} sm={3}>
              <div style={{marginLeft: '5px'}}>Postcode:</div>
            </Col>
            <Col sm={6}>
              <Field
                name="travellers[0].address.postcode"
                type="text"
                component={PostcodeControl}
                channel={this.props.channel}
                onSelectAddress={this.handleSelectAddress}
                validate={postcodeValidators}
              />
            </Col>
          </FormGroup>
        </Row>
        <Row className="margin-top margin-bottom">
          <FormGroup controlId="address">
            <Col style={{marginLeft: '5px'}} componentClass={ControlLabel} sm={3}>
              Address:
            </Col>
            <Col sm={6} style={{marginLeft: '-5px'}}>
              <Field
                className="line-top"
                name="travellers[0].address.line1"
                placeholder="Line 1"
                type="text"
                component={FieldFormControl}
                validate={[
                  isRequired,
                ]}
              />
              <Field
                className="line-middle"
                name="travellers[0].address.line2"
                placeholder="Line 2"
                type="text"
                component={FieldFormControl}
              />
              <Field
                className="line-middle"
                name="travellers[0].address.town"
                type="text"
                placeholder="Town"
                component={FieldFormControl}
              />
              <Field
                className="line-bottom"
                name="travellers[0].address.county"
                placeholder="County"
                type="text"
                component={FieldFormControl}
              />
            </Col>
          </FormGroup>
        </Row>
      </div>

    );
  }

  private renderFields(type: TravellerType) {
    const {fields, i18n, groupType, channel} = this.props;
    return this.getAll(type, fields).map((traveller, index) => {
      const fieldIndex = this.getIndex(traveller.id, fields);
      const dobValidator = type === 'adult' ? isAdult : () => {/*noop*/
      };
      return (
        <Row className="t-container" key={index}>
          <Col sm={12}>
            <Col sm={12} className="traveller margin-top">
              {index === 0 && type === 'adult' && (<h4>{i18n.quote.tripDetails.traveller.lead}</h4>)}
              {(index > 0 || type !== 'adult') && (<h4>{i18n.quote.tripDetails.traveller[type]} {index + 1}</h4>)}
              <Row>
                <Col sm={12}>
                  <FormGroup controlId="name">
                    <Col componentClass={ControlLabel} sm={3}>
                      {i18n.quote.tripDetails.traveller.name}:
                    </Col>
                    <Col sm={2}>
                      <Field
                        name={`travellers[${fieldIndex}].title`}
                        type="text"
                        placeholder="Title"
                        options={type === 'adult' ? adultTitles : childTitles}
                        component={SelectControl}
                        clearable={false}
                        validate={[isRequired]}
                      />
                    </Col>
                    <Col sm={4}>
                      <Field
                        name={`travellers[${fieldIndex}].firstName`}
                        type="text"
                        placeholder="First Name"
                        component={FieldFormControl}
                        validate={[isRequired]}
                      />
                    </Col>
                    <Col sm={3}>
                      <Field
                        name={`travellers[${fieldIndex}].lastName`}
                        type="text"
                        placeholder="Last Name"
                        component={FieldFormControl}
                        validate={[isRequired]}
                      />
                    </Col>
                  </FormGroup>
                </Col>
                {channel.key !== 'eurp-b2c' && (
                  <Col sm={12} style={{marginTop: '10px'}}>
                    <FormGroup controlId="dob">
                      <Col componentClass={ControlLabel} sm={3}>
                        {i18n.quote.tripDetails.traveller.dob}:
                      </Col>
                      <Col sm={4}>
                        <Field
                          name={`travellers[${fieldIndex}].dob`}
                          type="masked-date"
                          component={FieldFormControl}
                          placeholder="dd/mm/yyyy"
                          disabled={true}
                          className="form-control"
                          validate={[isRequired, isValidDate, dobValidator]}
                        />
                      </Col>
                      <Col sm={2} style={{marginTop: '6px'}}>
                        <Link to="/" className="edit-info-btn">
                          <FaPencil/>
                        </Link>
                      </Col>
                    </FormGroup>
                  </Col>
                )}
                <Col sm={12}>
                  {index === 0 && type === 'adult' && this.renderLeadTravellerFields(traveller, fieldIndex)}
                  {index === 0 && type === 'child' && groupType === 'independent_child' &&
                  this.renderLeadTravellerFields(traveller, fieldIndex)}
                </Col>
              </Row>
            </Col>
          </Col>
        </Row>
      );
    });
  }

  public componentWillMount() {
    const {fields} = this.props;
    const fieldLength = fields.length;

    if (fieldLength === 0) {
      this.addField('adult', fields);
    }
  }

  public render() {
    const {groupType} = this.props;
    const config = groupTypeTravellerMap[groupType ? groupType : 'individual'];

    return (
      <div className="section">
        <Row>
          <div className="traveller-section">
            {this.renderFields('adult')}
          </div>
          <div className={config.child.max === 0 ? 'hidden' : 'margin-top'}>
            <div className="traveller-section">
              {this.renderFields('child')}
            </div>
          </div>
        </Row>
      </div>
    );
  }
}

let selector;

export default flow([
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state) => {
      const groupType = selector(state, 'groupType');
      const tripType = selector(state, 'tripType');

      return {
        groupType,
        tripType,
      };
    },
  ),
])(TravellersInfo);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/scenes/PersonalDetails/components/TravellersInfo/index.tsx